import request from '../utils/request';

export interface StorageContainerListParams {
  page?: number;
  page_size?: number;
  warehouse_code?: string;
  project_code?: string;
  project?: string; // comma-separated project ids for multi-select
  customer?: string;
  customer_id?: string | number;
  container_type?: string;
  status?: string;
  current_location_code?: string;
  system_code?: string;
  inventory_unit_code?: string;
  sorting_code?: string;
  content_kind?: 'single' | 'mixed';
  putaway_status?: 'on' | 'off';
  ordering?: string;
  container_kind?: 'storage' | 'output';
  ioc_hierarchy_role?: 'child' | 'parent' | 'standalone';
  exclude_active_reservations?: boolean;
}

export async function fetchStorageContainers(params: StorageContainerListParams) {
  return request.get('/api/v1/storage/containers/', { params });
}

export async function fetchUnifiedContainers(params: StorageContainerListParams) {
  return request.get('/api/v1/storage/containers/unified/', { params });
}

// 仓位表专用：IOC 系统编码支持模糊匹配（SC 行为保持一致）
export async function fetchUnifiedContainersForLocation(params: StorageContainerListParams) {
  try {
    return await request.get('/api/v1/storage/containers/unified-location/', { params });
  } catch (e: any) {
    // 兼容后端尚未发布新接口的场景：回退到旧统一接口（避免页面 404 崩溃）
    if (Number(e?.response?.status) === 404) {
      return request.get('/api/v1/storage/containers/unified/', { params });
    }
    throw e;
  }
}

export async function downloadStorageContainers(params: StorageContainerListParams & { ordering?: string }) {
  return request.get('/api/v1/storage/containers/download/', { params, responseType: 'blob' });
}

export async function downloadUnifiedContainers(params: StorageContainerListParams & { ordering?: string }) {
  return request.get('/api/v1/storage/containers/unified/download/', { params, responseType: 'blob' });
}

// 仓位表专用下载：IOC 系统编码支持模糊匹配
export async function downloadUnifiedContainersForLocation(params: StorageContainerListParams & { ordering?: string }) {
  try {
    return await request.get('/api/v1/storage/containers/unified-location/download/', { params, responseType: 'blob' });
  } catch (e: any) {
    // 兼容后端尚未发布新接口的场景：回退到旧下载接口
    if (Number(e?.response?.status) === 404) {
      return request.get('/api/v1/storage/containers/unified/download/', { params, responseType: 'blob' });
    }
    throw e;
  }
}

// 获取仓位列表（可按仓库代码过滤）
export async function getLocations(params?: { warehouse_code?: string }) {
  const resp = await request.get('/api/v1/storage/locations/', { params });
  // 兼容返回结构 { success: boolean, data: [...] }
  const data = (resp.data && (resp.data.data || resp.data.results || resp.data)) as any;
  return Array.isArray(data) ? data : [];
}

export async function fetchStorageContainerDetailWithContents(containerId: number, params?: {
  c_page?: number; c_page_size?: number;
  h_page?: number; h_page_size?: number;
  m_page?: number; m_page_size?: number;
}) {
  return request.get(`/api/v1/storage/containers/${containerId}/detail_with_contents/`, { params });
}

export async function fetchUnifiedContainerDetail(
  containerKind: 'storage' | 'output',
  id: number,
  params?: {
    c_page?: number; c_page_size?: number;
    h_page?: number; h_page_size?: number;
    m_page?: number; m_page_size?: number;
  }
) {
  return request.get('/api/v1/storage/containers/unified/detail/', {
    params: {
      container_kind: containerKind,
      id,
      ...(params || {}),
    },
  });
}

export interface OutputContainerLabelPayload {
  container_code: string;
  instruction_id?: number;
  instruction_code?: string;
  project_summary?: string;
  label_variant?: 'normal' | 'exception';
  container_type_display?: string;
  created_at?: string;
  dims_cm?: { l?: string | number; w?: string | number; h?: string | number };
  dims_in?: { l?: string | number; w?: string | number; h?: string | number };
  weight?: { kg?: string | number; lb?: string | number };
  new_kinds?: number;
  total_qty?: number;
  details?: Array<{ new_code: string; qty: number }>;
}

export async function previewOutputContainerLabel(payload: OutputContainerLabelPayload) {
  return request.post('/api/v1/storage/output-containers/labels/preview/', payload, { responseType: 'blob' });
}

export async function downloadOutputContainerLabel(payload: OutputContainerLabelPayload) {
  return request.post('/api/v1/storage/output-containers/labels/download/', payload, { responseType: 'blob' });
}

// duplicate import removed

// 仓储容器类型
export interface StorageContainer {
  id: number;
  system_code: string;
  name: string;
  container_type: string;
  container_type_display: string;
  warehouse: number;
  warehouse_name: string;
  current_location: number | null;
  project: number;
  project_name: string;
  project_code: string;
  status: string;
  status_display: string;
  remarks: string;
  created_by: number;
  created_by_username: string;
  created_at: string;
  updated_at: string;
}

// 从分拣看板创建仓储容器的请求参数
export interface CreateContainerFromSortingBoardRequest {
  container_type: string;
  group_system_code: string;
  warehouse_code: string;
  remarks?: string;
  inbound_record_id: number;
  // 当容器类型为 direct（直接存储）时，必须提供存储仓位编码
  storage_location_code?: string;
  items: Array<{
    inventory_unit_code: string;
    quantity: number;
    splits?: Array<{
      inbound_record_id: number;
      qty: number;
      inbound_item_id?: number;
    }>;
  }>;
}

// 从分拣看板创建仓储容器
export async function createContainerFromSortingBoard(data: CreateContainerFromSortingBoardRequest) {
  try {
    const response = await request.post<{
      detail: string;
      container: StorageContainer;
      container_contents_count: number;
      sorting_board_updates: Array<{
        inventory_unit_code: string;
        quantity_reduced: number;
        remarks: string;
      }>;
    }>('/api/v1/storage/containers/create_from_sorting_board/', data);
    
    return response;
  } catch (error) {
    console.error('❌ [Storage Service] API调用失败');
    console.error('❌ [Storage Service] 错误详情:', error);
    throw error;
  }
}

// 添加到现有容器的请求参数
export interface AddToExistingContainerRequest {
  container_code: string;           // 目标容器编码
  group_system_code: string;        // 分拣组系统编码
  remarks?: string;                 // 备注信息
  inbound_record_id: number;        // 入库记录ID（必须）
  items: Array<{
    inventory_unit_code: string;
    quantity: number;
    splits?: Array<{
      inbound_record_id: number;
      qty: number;
      inbound_item_id?: number;
    }>;
  }>;
}

// 添加到现有容器
export async function addToExistingContainer(data: AddToExistingContainerRequest) {
  return request.post<{
    detail: string;
    container: StorageContainer;
    added_contents_count: number;
    updated_contents_count: number;
    sorting_board_updates: Array<{
      inventory_unit_code: string;
      quantity_reduced: number;
      remarks: string;
    }>;
  }>('/api/v1/storage/containers/add_to_existing/', data);
}

// 获取可用容器列表的请求参数
export interface GetAvailableContainersParams {
  warehouse_code?: string;
  project_code?: string;
  sorting_group_system_code?: string;
  limit?: number;
}

// 获取可用容器列表
export async function getAvailableContainers(params?: GetAvailableContainersParams) {
  const queryParams = new URLSearchParams();
  
  if (params?.warehouse_code) {
    queryParams.append('warehouse_code', params.warehouse_code);
  }
  if (params?.project_code) {
    queryParams.append('project_code', params.project_code);
  }
  if (params?.sorting_group_system_code) {
    queryParams.append('sorting_group_system_code', params.sorting_group_system_code);
  }
  if (params?.limit) {
    queryParams.append('limit', params.limit.toString());
  }
  
  const queryString = queryParams.toString();
  const url = `/api/v1/storage/containers/get_available_containers/${queryString ? `?${queryString}` : ''}`;
  
  return request.get<{
    containers: StorageContainer[];
    count: number;
  }>(url);
}